import { Injectable } from '@angular/core';
import { HelperService } from './helper.service';
import { DatabaseService } from './database.service';
import { StorageService } from './storage.service';
import { LocalAttachment } from './camera.service';


export interface INote {
  uid?: string;
  createdBy: string;
  creationDate: string;
  description: string;
  category?: string;
  id: string;
  lastModifiedDate: string;
  lastModifiedBy: string;
  organization: string;
  public: boolean;
  title: string;
  type: "grid" | "note";
  data?: any;
  columns?: any;
  attachments?: {
    [attachmentId: string]: any;
  }
}

export interface INoteCategory {
  title: string;
  value: string;
}

@Injectable({
    providedIn: 'root'
})
  

export class NotesService {

  constructor(
      private dbService: DatabaseService,
      private helperService: HelperService,
      private storageService: StorageService
  ) { }

  public getNoteCategories(): INoteCategory[] {
    return [
      {
        "title": "Nutrition",
        "value": "nutrition",
      },
      {
        "title": "Supplementation",
        "value": "supplementation",
      },
      {
        "title": "Training",
        "value": "trianing",
      }
      ,
      {
        "title": "Health",
        "value": "health",
      },
      {
        "title": "General",
        "value": "general",
      }
    ]
  }

  public getUserNotesList(userId: string) {
    return this.dbService.getListByRef<INote>(`notes/${userId}`, "id");
  }

  public async getUserNotesAsPromise(userId: string): Promise<INote[] | null> {
    const snap = await this.dbService.getSnapshot(`notes/${userId}`);
    return snap? <INote[]>this.helperService.transformFirebaseObjectToArray(snap) : null
  }

  public async getNoteAsPromise(userId: string, noteId: string): Promise<INote> {
    const snap = await this.dbService.getSnapshot(`notes/${userId}/${noteId}`);
    return snap? {...snap, id: noteId} : null
  }

  public async addUserNote(userId: string, note: Partial<INote>, attachments?: LocalAttachment[]): Promise<any> {
    const resp = await this.dbService.pushByRef(`notes/${userId}`, note);
    const noteId = <string>resp.key;
    if (attachments && attachments.length > 0) {
      await this.processUserNoteAttachments(noteId, userId, attachments);
    }
    return noteId;
  }

  public async updateUserNote(userId: string, noteId: string, note: Partial<INote>, attachments?: LocalAttachment[]): Promise<any> {
    const res = await this.dbService.updateRef(`notes/${userId}/${noteId}`, note);
    if (attachments && attachments.length > 0) {
      await this.processUserNoteAttachments(noteId, userId, attachments);
    }
    return res
  }

  public deleteUserNote(userId: string, noteId: string) {
    return this.dbService.removeRef(`notes/${userId}/${noteId}`);
  }

  async processUserNoteAttachments(noteId: string,  clientId: string, attachments: LocalAttachment[] = []) {
    const promises: Promise<any>[] = [];
    const attachmentIds: string[] = [];
    for (const attachment of attachments) {
      promises.push(
        this.addUserNoteAttachment(
          clientId, 
          noteId, 
          attachment
        ).then((attachmentId) => {
        attachmentIds.push(attachmentId)
      }));
    }
    await Promise.all(promises)
  }

  async addUserNoteAttachment(userId: string, noteId: string, attachment: LocalAttachment): Promise<any> {
    const storagePath = `users/${userId}/notes/${noteId}/${attachment.name}`;
    let attachmentRef: string;

    if(attachment.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    if(!attachmentRef) {
      throw new Error("Failed to upload attachment");
    }

    const resp = await this.dbService.pushByRef(`/notes/${userId}/${noteId}/attachments`, {
      creationDate: new Date().toISOString(),
      url: attachmentRef,
      fileName: attachment.name,
      type: attachment.type,
    });
    return resp.key
  }

  // Org Notes
  public async getOrgNoteAsPromise(orgId: string, noteId: string): Promise<INote> {
    const snap = await this.dbService.getSnapshot(`organizationNotes/${orgId}/${noteId}`);
    return snap? {...snap, id: noteId} : null
  }

  public async addOrgNote(orgId: string, note: Partial<INote>, attachments?: any[]): Promise<any> {
    const resp = await this.dbService.pushByRef(`organizationNotes/${orgId}`, note);
    const noteId = <string>resp.key;
    if (attachments && attachments.length > 0) {
      await this.processOrgNoteAttachments(noteId, orgId, attachments);
    }
    return noteId;
  }

  public async updateOrgNote(orgId: string, noteId: string, note: Partial<INote>, attachments?: LocalAttachment[]): Promise<any> {
    const res = await this.dbService.updateRef(`organizationNotes/${orgId}/${noteId}`, note);
    if (attachments && attachments.length > 0) {
      await this.processOrgNoteAttachments(noteId, orgId, attachments);
    }
    return res
  }

  public deleteOrgNote(orgId: string, noteId: string) {
    return this.dbService.removeRef(`organizationNotes/${orgId}/${noteId}`);
  }

  async processOrgNoteAttachments(noteId: string,  orgId: string, attachments: LocalAttachment[] = []) {
    const promises: Promise<any>[] = [];
    const attachmentIds: string[] = [];
    for (const attachment of attachments) {
      promises.push(
        this.addOrgNoteAttachment(
          orgId, 
          noteId, 
          attachment
        ).then((attachmentId) => {
        attachmentIds.push(attachmentId)
      }));
    }
    await Promise.all(promises)
  }

  async addOrgNoteAttachment(orgId: string, noteId: string, attachment: LocalAttachment): Promise<any> {

    const storagePath = `organizations/${orgId}/notes/${noteId}/${attachment.name}`;
    let attachmentRef: string;

    if(attachment.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    if(!attachmentRef) {
      throw new Error("Failed to upload attachment");
    }
    
    const resp = await this.dbService.pushByRef(`/organizationNotes/${orgId}/${noteId}/attachments`, {
      creationDate: new Date().toISOString(),
      url: attachmentRef,
      fileName: attachment.name,
      type: attachment.type,
    });
    return resp.key
  }
}
